/**
 * Pay now (`/account/bookings/:bookingId/pay`, web-sign-in-checkout D8): an
 * approved request paid by card on the website, as the app's post-approval
 * payment does: the booking's ticket, then the payment step for its amount;
 * once paid, the booked page. A booking with nothing to pay goes to its booked
 * page; one that is not this client's shows the studio's not-found page. A
 * booking the studio made that still owes forms goes to its Complete step
 * first, as the app's Pay now does (manual-booking-redesign D9; Codex review
 * 2026-10-26).
 */
import { useParams } from 'react-router-dom';
import { bookingDetailHeroProps } from '@/pages/booking/shared/bookingRules';

import { useWebSession } from '../api/session';
import { getClientTimezone } from '../format';
import { bookedRows, useWebBooking } from '../hooks/useWebBooking';
import { MoveTo, usePageMover, useSiteBack } from '../pageMover';
import { Ticket } from '../pieces/booked/BookedView';
import css from '../pieces/booked/Booked.module.css';
import { PaymentStep } from '../pieces/payment-step';
import { PhonePayment } from '../phone/PhoneBookingFrame';
import { SignInStep } from '../pieces/sign-in-step';
import shared from '../styles/shared.module.css';
import { bookingPhoto } from '../ui/Photo';
import { useAppScreens } from '../ui/useIsPhone';
import { usePageTitle } from '../ui/usePageTitle';
import { NotFoundPage } from './NotFoundPage';

export function PayNowPage() {
  const { bookingId = '' } = useParams<{ bookingId: string }>();
  const session = useWebSession();
  const { detail } = useWebBooking(bookingId);
  usePageTitle('Pay');
  const phone = useAppScreens();
  const onBack = useSiteBack();
  const navigate = usePageMover();
  // As the app's Pay now: too close to the start to pay, Pick a new time returns to the booking.
  const toBooking = () => navigate(`/account/bookings/${encodeURIComponent(bookingId)}`, { replace: true });

  if (session.status === 'signedOut') {
    return (
      <div className={`${shared.wrap} ${css.signInWrap}`}>
        <SignInStep />
      </div>
    );
  }
  if (detail.isError) return <NotFoundPage />;
  const b = detail.data;
  if (!b) return null;
  const amount = b.payment?.amount ?? null;
  if (b.status !== 'PENDING_PAYMENT' || !amount) return <MoveTo to={`/booked/${encodeURIComponent(bookingId)}`} />;
  // Coming from Complete booking, the read held may predate the form just sent: decide on the fresh read, not that one.
  if (b.completeBy && (b.outstanding?.forms ?? 0) > 0) {
    return detail.isFetching ? null : <MoveTo to={`/account/bookings/${encodeURIComponent(bookingId)}/complete`} />;
  }
  // Below 768px, the app's payment page (design D12).
  if (phone) return <PhonePayment entry={{ kind: 'postApproval', bookingId }} amount={amount} hero={bookingDetailHeroProps(b, getClientTimezone(), false)} until={null} onBack={onBack} onPickNewTime={toBooking} />;

  return (
    <div className={`${css.c2} ${css.pay}`}>
      <div className={css.head}>
        <h1 className={`${css.title} ${css.serif}`}>Complete payment</h1>
      </div>
      {/* The amount is on the button; the ticket keeps when, length and where. */}
      <Ticket
        name={b.service.name}
        photo={bookingPhoto(b.service)}
        withName={b.provider?.name ? `with ${b.provider.name}` : null}
        rows={bookedRows(b, getClientTimezone(), false).slice(0, -1)}
      />
      <div className={css.payNow}>
        <PaymentStep entry={{ kind: 'postApproval', bookingId }} amount={amount} onPickNewTime={toBooking} />
      </div>
    </div>
  );
}
